import { UploadOutlined } from "@ant-design/icons";
import { Button, message, Modal, notification, Spin, Table, Upload } from "antd";
import React, { useEffect, useState } from "react";
import {
  read as XLSXread,
  utils as XLSXutils,
  WorkBook as XLSXWorkBook,
  WorkSheet as XLSXWorkSheet,
} from "xlsx";
import { POST } from "../../../services";
import { UPLIFT_CREATE_CUTTING } from "../../../services/path";

interface MyModalProps {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  width?: string | number;
  centered?: boolean;
  bodyStyle?: React.CSSProperties;
  children?: React.ReactNode;
  user?: any; // Optional prop for user context
}

const ImportExcelModal: React.FC<MyModalProps> = ({
  open,
  onClose,
  onCancel,
  width = "50%",
  centered = true,
  bodyStyle = {},
  children,
  user,
}) => {
  interface ExcelRow {
    SKU: string;
    Alter_SKU: string;
    Alter_Name: string;
    Cutting_ID: string;
  }

  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isSavingFile, setIsSavingFile] = useState(false);
  const [data, setData] = useState<ExcelRow[]>([]);
  const handleFileChange = (file: File) => {
    setIsUploadingFile(true); // Set uploading state to true
    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target) {
        notification.error({
          message: "นำเข้าไฟล์ไม่สำเร็จ",
          description: "โปรดอัพโหลดไฟล์ Excel ที่ถูกต้อง",
          duration: 2,
        });
        return;
      }

      const binaryStr = (e.target as FileReader).result as string;
      const workbook: XLSXWorkBook = XLSXread(binaryStr, { type: "binary" });
      const firstSheetName: string = workbook.SheetNames[0];
      const worksheet: XLSXWorkSheet = workbook.Sheets[firstSheetName];
      const jsonData: ExcelRow[] = XLSXutils.sheet_to_json<ExcelRow>(worksheet);
      setData(jsonData); // Set the data to state
      if (jsonData.length === 1) {
        const firstRow = jsonData[0];
        const isEmptyRow =
          firstRow.SKU === undefined ||
          firstRow.SKU === "" ||
          firstRow.Alter_SKU === undefined ||
          firstRow.Alter_SKU === "" ||
          firstRow.Alter_Name === undefined ||
          firstRow.Alter_Name === "" ||
          firstRow.Cutting_ID === undefined ||
          firstRow.Cutting_ID === "";
        if (isEmptyRow) {
          message.error({
            content: "โปรดตรวจสอบไฟล์ Excel ของคุณ",
            duration: 2,
          });

          setData([]); // Clear data if the row is empty
          return;
        }
      }

      setIsUploadingFile(false); // Set uploading state to false after processing
      message.success({
        content: "นำเข้าไฟล์สำเร็จ",
        duration: 2,
      });
    };

    reader.onerror = () => {
      message.error({
        content: "นำเข้าไฟล์ไม่สำเร็จ",
        duration: 2,
      });
    };

    reader.readAsBinaryString(file); // Read the file as binary string
  };

  const confirmClose = () => {
    Modal.confirm({
      title: "ต้องการปิดหน้าต่างนี้ไหม",
      content: "หากไม่กดบันทึก ข้อมูลจะไม่ถูกแก้ไข",
      onOk() {
        onClose();
      },
    });
  };

  useEffect(() => {
    if (!open) {
      setData([]); // Clear data when modal is closed
    }
  }, [open]);

  const handleUploadChange = (info: any) => {
    if (info.file.status === "done") {
      handleFileChange(info.file.originFileObj); // Process the uploaded file
    } else if (info.file.status === "error") {
      message.error(`ไม่สามารถอัพโหลดไฟล์ ${info.file.name} ได้`);
    }
  };

  const handleSave = async () => {
    setIsSavingFile(true); // Set saving state to true
    const importData = data.map((row) => {
      return {
        sku: row.SKU,
        alter_sku: row.Alter_SKU,
        alter_name: row.Alter_Name,
        cutting_id: row.Cutting_ID,
      };
    });

    const payload = {
      master_cutting: importData,
    };

    try {
      const respone = await POST(UPLIFT_CREATE_CUTTING, payload);
      if (respone) {
        message.success("บันทึกข้อมูล Code G สำเร็จ");
        setIsSavingFile(false);
        onClose();
      } else {
        message.error("ไม่สามารถบันทึกข้อมูล Code G ได้");
      }
    } catch (error) {
      message.error("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
    } finally {
      setIsSavingFile(false); // Set saving state to false after processing
    }
  };

  const beforeUpload = (file: File) => {
    const isExcel =
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel";
    if (!isExcel) {
      message.error("โปรดอัพโหลดเฉพาะไฟล์ Excel เท่านั้น");
    }

    return isExcel; // Prevent upload if not an Excel file
  };

  return (
    <div>
      <Modal
        title="Import Excel File"
        open={open}
        onOk={handleSave}
        onCancel={confirmClose}
        width={width}
        centered={centered}
        loading={isSavingFile}
        styles={{ body: bodyStyle }}
      >
        <Upload
          beforeUpload={beforeUpload}
          accept=".xlsx, .xls" // Accept only Excel files
          showUploadList={false} // Hide the default upload list
          onChange={handleUploadChange}
          customRequest={({ file, onSuccess }) => {
            handleFileChange(file as File); // Process the file directly
          }}
        >
          <Button icon={<UploadOutlined />} loading={isUploadingFile}>
            Click to Upload
          </Button>
        </Upload>
        <div style={{ maxHeight: 400, overflowY: "auto", width: "100%" }}>
          {isUploadingFile && (
            <Spin size="large" style={{ position: "absolute", top: "50%", left: "50%" }} />
          )}
          <Table
            dataSource={data}
            columns={[
              { title: "SKU", dataIndex: "SKU", key: "SKU" },
              { title: "SKU Name", dataIndex: "Alter_SKU", key: "Alter_SKU" },
              {
                title: "Alter Name",
                dataIndex: "Alter_Name",
                key: "Alter_Name",
              },
              {
                title: "Cutting ID",
                dataIndex: "Cutting_ID",
                key: "Cutting_ID",
              },
            ]}
            rowKey="SKU" // Assuming SKU is unique
            pagination={false}
            style={{ marginTop: 10 }} // Add some margin for better spacing
          />
        </div>
      </Modal>
    </div>
  );
};

export default ImportExcelModal;
